<script setup>
/**
 * "Something changed? Tell Waslo." (spec P-8.3), on the Overview: one box, typed or spoken, and
 * Waslo proposes the concrete changes across every page they live on. Proposing is priced and
 * confirmed first (`what_changed`); nothing changes until she applies, in `SuggestionReview`.
 *
 * A proposal she has not decided on is shown again on the next visit (the newest open one), so a
 * paid proposal is never bought twice.
 */
import { computed, onMounted, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AiConfirmInline from './AiConfirmInline.vue';
import SuggestionReview from './SuggestionReview.vue';
import VoiceNoteButton from './VoiceNoteButton.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useWasloI18n } from '../i18n.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const { t, locale } = useWasloI18n();
const price = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'what_changed',
});

const LIMIT = 2000;
const text = ref('');
const busy = ref(false);
const failure = ref('');
const nothing = ref(false);
const suggestion = ref(null);

const loadOpen = async () => {
  if (props.accountId === null) return;
  try {
    const open = await wasloApi.getSetupSuggestions(
      props.accountId,
      'what_changed'
    );
    suggestion.value = open[0] ?? null;
  } catch {
    // The box still works; an open proposal it could not read is shown next time.
  }
};
onMounted(loadOpen);
watch(() => props.accountId, loadOpen);

const proposeLabel = computed(() =>
  busy.value
    ? t('WASLO.SETUP_AI.WHAT_CHANGED.PROPOSING')
    : price.label(t('WASLO.SETUP_AI.WHAT_CHANGED.PROPOSE'))
);

const propose = async () => {
  const said = text.value.trim();
  if (said === '' || busy.value || props.accountId === null) return;
  busy.value = true;
  failure.value = '';
  nothing.value = false;
  try {
    const outcome = await price.confirmAndRun({
      run: credits =>
        wasloApi.whatChanged(
          props.accountId,
          {
            text: said,
            lang: String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en',
          },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    suggestion.value = outcome.result.suggestion;
    nothing.value = outcome.result.suggestion === null;
    if (outcome.result.suggestion !== null) text.value = '';
  } catch (error) {
    failure.value =
      error?.status === 422
        ? t('WASLO.SETUP_AI.WHAT_CHANGED.UNUSABLE')
        : error?.message || t('WASLO.SETUP_AI.WHAT_CHANGED.FAILED');
  } finally {
    busy.value = false;
  }
};

const addSpoken = said => {
  text.value = [text.value.trim(), said]
    .filter(Boolean)
    .join(' ')
    .slice(0, LIMIT);
};

const dismissed = () => {
  suggestion.value = null;
};
</script>

<template>
  <section class="flex flex-col gap-4" data-waslo-what-changed>
    <SettingsHeader
      :heading="t('WASLO.SETUP_AI.WHAT_CHANGED.HEADING')"
      :description="t('WASLO.SETUP_AI.WHAT_CHANGED.DESCRIPTION')"
    />
    <SuggestionReview
      v-if="suggestion !== null"
      :account-id="accountId"
      :suggestion="suggestion"
      @dismissed="dismissed"
    />
    <template v-else>
      <WasloTextArea
        v-model="text"
        :placeholder="t('WASLO.SETUP_AI.WHAT_CHANGED.PLACEHOLDER')"
        :max-length="LIMIT"
        :show-count="false"
        min-height="4rem"
        :disabled="busy"
        data-waslo-what-changed-text
      />
      <VoiceNoteButton :disabled="busy" @text="addSpoken" />
      <div class="flex">
        <Button
          :label="proposeLabel"
          :disabled="busy || text.trim() === ''"
          data-waslo-what-changed-propose
          @click="propose"
        />
      </div>
      <AiConfirmInline :controller="price" />
      <p
        v-if="nothing"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-what-changed-nothing
      >
        {{ t('WASLO.SETUP_AI.WHAT_CHANGED.NOTHING') }}
      </p>
    </template>
    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-what-changed-failure
    >
      {{ failure }}
    </p>
  </section>
</template>
